import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
	ArrowLeft,
	Building2,
	CalendarPlus,
	Eraser,
	FlaskConical,
	Gem,
	Route,
	Sparkles,
	Stethoscope,
	Trash2,
	UserPlus,
} from 'lucide-react';
import { PageTitle } from '@/components/ui';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import {
	useGetSandboxCatalog,
	useGetSandboxScenario,
} from '@/services/queries/sandbox';
import {
	useDeleteSandboxScenario,
	useResetSandboxScenario,
	useSandboxCancelSubscription,
	useSandboxSessionAction,
} from '@/services/mutations/sandbox';
import { getBackendErrorMessage } from '@/utils/errorMessage';
import { SandboxUserModal } from './components/SandboxUserModal';
import { SandboxProfessionalModal } from './components/SandboxProfessionalModal';
import { SandboxSubscriptionModal } from './components/SandboxSubscriptionModal';
import { SandboxSessionModal } from './components/SandboxSessionModal';
import { SandboxMatchModal } from './components/SandboxMatchModal';
import { SandboxCompanyModal } from './components/SandboxCompanyModal';
import { SandboxJourneyModal } from './components/SandboxJourneyModal';
import {
	SandboxImpersonateModal,
	type ImpersonationTarget,
} from './components/SandboxImpersonateModal';
import { SandboxTimeline } from './components/SandboxTimeline';
import {
	ActionButton,
	SandboxCompaniesSection,
	SandboxProfessionalsSection,
	SandboxSessionsSection,
	SandboxSubscriptionsSection,
	SandboxUsersSection,
} from './components/SandboxSections';
import { ENTITY_LABELS, ENTITY_ORDER, formatDateTime } from './sandboxLabels';

type OpenModal =
	| { kind: 'user' }
	| { kind: 'professional' }
	| { kind: 'subscription'; userId?: string }
	| { kind: 'session'; userId?: string; professionalId?: string }
	| { kind: 'match'; userId?: string }
	| { kind: 'company' }
	| { kind: 'journey' }
	| { kind: 'reset' }
	| { kind: 'delete' }
	| null;

/**
 * Admin › Sandbox › Cenário: o "tabuleiro" da simulação. À esquerda, as
 * entidades vivas com as ações possíveis em cada uma; à direita, a linha do
 * tempo do que já aconteceu.
 */
const SandboxScenarioPage = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { data: catalog } = useGetSandboxCatalog();
	const { data, isLoading, error } = useGetSandboxScenario(id);
	const [modal, setModal] = useState<OpenModal>(null);
	const [impersonation, setImpersonation] =
		useState<ImpersonationTarget | null>(null);
	const [pendingSessionId, setPendingSessionId] = useState<string | null>(
		null,
	);
	const [pendingSubscriptionId, setPendingSubscriptionId] = useState<
		string | null
	>(null);

	const scenarioId = id ?? '';
	const { mutate: runSessionAction } = useSandboxSessionAction(
		scenarioId,
		() => setPendingSessionId(null),
	);
	const { mutate: cancelSubscription } = useSandboxCancelSubscription(
		scenarioId,
		() => setPendingSubscriptionId(null),
	);
	const { mutate: resetScenario, isPending: isResetting } =
		useResetSandboxScenario(() => setModal(null));
	const { mutate: deleteScenario, isPending: isDeleting } =
		useDeleteSandboxScenario((report) => {
			setModal(null);
			if (report.errors.length === 0) navigate('/admin/sandbox');
		});

	const sessionAction = (action: Parameters<typeof runSessionAction>[0]) => {
		setPendingSessionId(action.sessionId);
		runSessionAction(action, {
			onError: () => setPendingSessionId(null),
		});
	};

	if (isLoading || (!catalog && !error)) {
		return (
			<section className="px-8 py-8">
				<p className="text-sm text-stone-400">Carregando cenário...</p>
			</section>
		);
	}

	if (error || !data || !catalog) {
		return (
			<section className="px-8 py-8">
				<div className="rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 p-10 text-center">
					<h3 className="text-base font-black text-stone-900">
						Cenário indisponível
					</h3>
					<p className="mx-auto mt-2 max-w-md text-sm text-stone-400">
						{(error && getBackendErrorMessage(error)) ??
							'Este cenário não existe mais ou o sandbox está desligado.'}
					</p>
					<Link
						to="/admin/sandbox"
						className="mt-6 inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm font-bold text-white"
					>
						Voltar para o Sandbox
					</Link>
				</div>
			</section>
		);
	}

	const { scenario, counts, users, professionals, subscriptions, sessions } =
		data;
	const totalEntities = ENTITY_ORDER.reduce(
		(acc, type) => acc + counts[type],
		0,
	);

	const toolbarButton =
		'inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-700 transition-colors hover:border-stone-900 hover:text-stone-900';

	return (
		<section className="space-y-6 px-8 py-8">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="space-y-2">
					<Link
						to="/admin/sandbox"
						className="inline-flex items-center gap-1.5 text-xs font-bold text-stone-400 transition-colors hover:text-stone-900"
					>
						<ArrowLeft className="h-3.5 w-3.5" />
						Sandbox
					</Link>
					<div className="flex items-center gap-3">
						<span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-stone-900 text-white">
							<FlaskConical className="h-5 w-5" />
						</span>
						<PageTitle text={scenario.name} />
					</div>
					<p className="max-w-2xl text-sm text-stone-400">
						{scenario.description ??
							'Sem descrição. Use as ações abaixo para simular a plataforma.'}
					</p>
					<div className="flex flex-wrap gap-1.5">
						{ENTITY_ORDER.map((type) => (
							<span
								key={type}
								className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
									counts[type] > 0
										? 'bg-stone-900 text-white'
										: 'bg-stone-100 text-stone-400'
								}`}
							>
								{counts[type]}{' '}
								{ENTITY_LABELS[type].toLowerCase()}
							</span>
						))}
						<span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold text-stone-400">
							atualizado {formatDateTime(scenario.updated_at)}
						</span>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={() => setModal({ kind: 'journey' })}
						className="inline-flex items-center gap-2 rounded-full bg-brand-pink px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-pink/90"
					>
						<Route className="h-3.5 w-3.5" />
						Jornada guiada
					</button>
					<button
						type="button"
						onClick={() => setModal({ kind: 'reset' })}
						disabled={totalEntities === 0}
						className={`${toolbarButton} disabled:opacity-40`}
					>
						<Eraser className="h-3.5 w-3.5" />
						Limpar dados
					</button>
					<button
						type="button"
						onClick={() => setModal({ kind: 'delete' })}
						className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-xs font-bold text-rose-600 transition-colors hover:border-rose-600"
					>
						<Trash2 className="h-3.5 w-3.5" />
						Apagar cenário
					</button>
				</div>
			</div>

			<div className="rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
				<p className="text-[11px] font-black uppercase tracking-wide text-stone-400">
					Simular
				</p>
				<div className="mt-3 flex flex-wrap gap-2">
					<button
						type="button"
						className={toolbarButton}
						onClick={() => setModal({ kind: 'user' })}
					>
						<UserPlus className="h-3.5 w-3.5" /> Criar usuário
					</button>
					<button
						type="button"
						className={toolbarButton}
						onClick={() => setModal({ kind: 'professional' })}
					>
						<Stethoscope className="h-3.5 w-3.5" /> Criar
						profissional
					</button>
					<button
						type="button"
						className={toolbarButton}
						onClick={() => setModal({ kind: 'subscription' })}
						disabled={users.length === 0}
					>
						<Gem className="h-3.5 w-3.5" /> Ativar assinatura
					</button>
					<button
						type="button"
						className={toolbarButton}
						onClick={() => setModal({ kind: 'session' })}
						disabled={
							users.length === 0 || professionals.length === 0
						}
					>
						<CalendarPlus className="h-3.5 w-3.5" /> Agendar sessão
					</button>
					<button
						type="button"
						className={toolbarButton}
						onClick={() => setModal({ kind: 'match' })}
						disabled={users.length === 0}
					>
						<Sparkles className="h-3.5 w-3.5" /> Responder match
					</button>
					<button
						type="button"
						className={toolbarButton}
						onClick={() => setModal({ kind: 'company' })}
					>
						<Building2 className="h-3.5 w-3.5" /> Criar empresa
					</button>
				</div>
			</div>

			<div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
				<div className="space-y-8">
					<SandboxUsersSection
						users={users}
						onCreate={() => setModal({ kind: 'user' })}
						onSubscribe={(userId) =>
							setModal({ kind: 'subscription', userId })
						}
						onSchedule={(userId) =>
							setModal({ kind: 'session', userId })
						}
						onMatch={(userId) =>
							setModal({ kind: 'match', userId })
						}
						onImpersonate={setImpersonation}
					/>
					<SandboxProfessionalsSection
						professionals={professionals}
						onCreate={() => setModal({ kind: 'professional' })}
						onSchedule={(professionalId) =>
							setModal({ kind: 'session', professionalId })
						}
						onImpersonate={setImpersonation}
					/>
					<SandboxSubscriptionsSection
						subscriptions={subscriptions}
						onCreate={() => setModal({ kind: 'subscription' })}
						pendingId={pendingSubscriptionId}
						onCancel={(subscriptionId) => {
							setPendingSubscriptionId(subscriptionId);
							cancelSubscription(subscriptionId, {
								onError: () => setPendingSubscriptionId(null),
							});
						}}
					/>
					<SandboxSessionsSection
						sessions={sessions}
						onCreate={() => setModal({ kind: 'session' })}
						pendingId={pendingSessionId}
						onPay={(sessionId, mode) =>
							sessionAction({ type: 'pay', sessionId, mode })
						}
						onStart={(sessionId) =>
							sessionAction({ type: 'start', sessionId })
						}
						onEnd={(sessionId) =>
							sessionAction({ type: 'end', sessionId })
						}
						onCancel={(sessionId, by) =>
							sessionAction({ type: 'cancel', sessionId, by })
						}
					/>
					<SandboxCompaniesSection
						companies={data.companies}
						onCreate={() => setModal({ kind: 'company' })}
					/>
					{totalEntities === 0 ? (
						<div className="rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 p-8 text-center">
							<p className="text-sm font-bold text-stone-700">
								Cenário vazio
							</p>
							<p className="mx-auto mt-1 max-w-md text-xs text-stone-400">
								Rode a jornada guiada para ver tudo acontecer de
								uma vez, ou crie um usuário e um profissional e
								monte o fluxo passo a passo.
							</p>
							<div className="mt-4">
								<ActionButton
									tone="primary"
									onClick={() =>
										setModal({ kind: 'journey' })
									}
								>
									<Route className="h-3 w-3" /> Rodar jornada
									guiada
								</ActionButton>
							</div>
						</div>
					) : null}
				</div>

				<div className="xl:sticky xl:top-6 xl:self-start">
					<SandboxTimeline events={data.events} />
				</div>
			</div>

			<SandboxUserModal
				scenarioId={scenarioId}
				catalog={catalog}
				isOpen={modal?.kind === 'user'}
				onClose={() => setModal(null)}
			/>
			<SandboxProfessionalModal
				scenarioId={scenarioId}
				catalog={catalog}
				isOpen={modal?.kind === 'professional'}
				onClose={() => setModal(null)}
			/>
			<SandboxSubscriptionModal
				scenarioId={scenarioId}
				catalog={catalog}
				users={users}
				initialUserId={
					modal?.kind === 'subscription' ? modal.userId : null
				}
				isOpen={modal?.kind === 'subscription'}
				onClose={() => setModal(null)}
			/>
			<SandboxSessionModal
				scenarioId={scenarioId}
				catalog={catalog}
				users={users}
				professionals={professionals}
				initialUserId={modal?.kind === 'session' ? modal.userId : null}
				initialProfessionalId={
					modal?.kind === 'session' ? modal.professionalId : null
				}
				isOpen={modal?.kind === 'session'}
				onClose={() => setModal(null)}
			/>
			<SandboxMatchModal
				scenarioId={scenarioId}
				catalog={catalog}
				users={users}
				initialUserId={modal?.kind === 'match' ? modal.userId : null}
				isOpen={modal?.kind === 'match'}
				onClose={() => setModal(null)}
			/>
			<SandboxCompanyModal
				scenarioId={scenarioId}
				catalog={catalog}
				users={users}
				isOpen={modal?.kind === 'company'}
				onClose={() => setModal(null)}
			/>
			<SandboxJourneyModal
				scenarioId={scenarioId}
				catalog={catalog}
				professionals={professionals}
				isOpen={modal?.kind === 'journey'}
				onClose={() => setModal(null)}
			/>
			<SandboxImpersonateModal
				scenarioId={scenarioId}
				target={impersonation}
				onClose={() => setImpersonation(null)}
			/>

			<ConfirmActionModal
				isOpen={modal?.kind === 'reset'}
				onClose={() => (isResetting ? undefined : setModal(null))}
				onConfirm={() => resetScenario(scenarioId)}
				title="Limpar os dados do cenário?"
				description="Apaga todos os usuários, profissionais, assinaturas, sessões e empresas criados aqui. O cenário continua existindo, vazio."
				confirmButtonText={isResetting ? 'Limpando...' : 'Limpar dados'}
				disableConfirm={isResetting}
			/>
			<ConfirmActionModal
				isOpen={modal?.kind === 'delete'}
				onClose={() => (isDeleting ? undefined : setModal(null))}
				onConfirm={() => deleteScenario(scenarioId)}
				title="Apagar cenário?"
				description="Apaga o cenário e TODOS os dados de teste criados por ele. Não afeta nada fora do cenário."
				confirmButtonText={isDeleting ? 'Apagando...' : 'Apagar tudo'}
				disableConfirm={isDeleting}
			/>
		</section>
	);
};

export { SandboxScenarioPage };
